import React, { useState } from 'react'

const PhoneInput = ({ 
    label, 
    name, 
    value, 
    onChange, 
    error, 
    message, 
    className, 
    disabled = false,
    placeholder = "e.g., +61412345678" 
}) => {
    const [countryCode, setCountryCode] = useState('+61') // Default to Australia
    const [phoneNumber, setPhoneNumber] = useState('')

    // Parse existing value if provided
    React.useEffect(() => {
        if (value && value.startsWith('+')) {
            // Extract country code (assume 2-3 digits after +)
            const match = value.match(/^(\+\d{1,3})(.*)$/)
            if (match) {
                setCountryCode(match[1])
                setPhoneNumber(match[2])
            }
        } else if (value) {
            setPhoneNumber(value)
        }
    }, [value])

    const handleCountryCodeChange = (e) => {
        const newCountryCode = e.target.value
        setCountryCode(newCountryCode)
        const fullNumber = newCountryCode + phoneNumber
        onChange({ target: { name, value: fullNumber } })
    }

    const handlePhoneNumberChange = (e) => {
        const newPhoneNumber = e.target.value.replace(/[^\d]/g, '') // Only digits
        setPhoneNumber(newPhoneNumber)
        const fullNumber = countryCode + newPhoneNumber
        onChange({ target: { name, value: fullNumber } })
    }

    const validatePhoneNumber = (fullNumber) => {
        // International phone number pattern: + followed by country code and number
        const pattern = /^\+[1-9]\d{1,14}$/
        return pattern.test(fullNumber)
    }

    const isValid = validatePhoneNumber(countryCode + phoneNumber)
    const showValidation = phoneNumber.length > 0

    const countryOptions = [
        { code: '+61', name: 'Australia', flag: '🇦🇺' },
        { code: '+1', name: 'USA/Canada', flag: '🇺🇸' },
        { code: '+44', name: 'UK', flag: '🇬🇧' },
        { code: '+33', name: 'France', flag: '🇫🇷' },
        { code: '+49', name: 'Germany', flag: '🇩🇪' },
        { code: '+81', name: 'Japan', flag: '🇯🇵' },
        { code: '+86', name: 'China', flag: '🇨🇳' },
        { code: '+91', name: 'India', flag: '🇮🇳' },
    ]

    return (
        <div className={`${className}`}>
            {label && (
                <label className="mb-2.5 block text-black dark:text-white">
                    {label} <span className="text-meta-1">*</span>
                </label>
            )}
            <div className="flex">
                {/* Country Code Selector */}
                <select
                    value={countryCode}
                    onChange={handleCountryCodeChange}
                    disabled={disabled}
                    className="w-32 rounded-l-lg border-[1.5px] border-stroke bg-transparent py-3 px-3 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                >
                    {countryOptions.map((country) => (
                        <option key={country.code} value={country.code}>
                            {country.flag} {country.code}
                        </option>
                    ))}
                </select>

                {/* Phone Number Input */}
                <input
                    type="tel"
                    name={name}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    disabled={disabled}
                    placeholder="412345678"
                    className={`flex-1 rounded-r-lg border-[1.5px] border-l-0 bg-transparent py-3 px-5 text-black outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter dark:bg-form-input dark:text-white dark:focus:border-primary ${
                        error ? 'border-meta-1' : 'border-stroke dark:border-form-strokedark'
                    }`}
                />
            </div>

            {/* Validation Messages */}
            <div className="mt-1">
                {showValidation && !isValid && (
                    <p className="text-sm text-meta-1">
                        Please enter a valid international phone number
                    </p>
                )}
                {showValidation && isValid && (
                    <p className="text-sm text-meta-3">
                        ✓ Valid phone number format
                    </p>
                )}
                {error && message && (
                    <p className="text-sm text-meta-1">{message}</p>
                )}
            </div>

            {/* Format Help */}
            <div className="mt-1">
                <p className="text-xs text-gray-500">
                    Format: {countryCode}XXXXXXXXX (no spaces or dashes)
                </p>
            </div>
        </div>
    )
}

export default PhoneInput
